/**
 * The two things on these screens that change without anybody pressing a
 * button, and how often to ask about them.
 *
 * Everything else is refreshed by the action that changed it — `useAction`
 * invalidates the lists after every mutation — so polling it is asking a
 * question whose answer cannot have moved. That is what this exists to stop.
 *
 * Both default to OFF. A console nobody is looking at should cost nothing, and
 * an idle tab used to cost 84 requests a minute. Turn them on per deployment:
 *
 *   MODELS_LIVE_HARDWARE=1          MODELS_LIVE_HARDWARE_SECONDS=4
 *   MODELS_LIVE_RUNNING=1           MODELS_LIVE_RUNNING_SECONDS=10
 *
 * When they are off, the numbers still arrive — on load, on a focused tab, and
 * after anything you do. They simply do not tick on their own, and the Refresh
 * button is how you ask again.
 */
import { useQuery } from "@tanstack/react-query";

import { fetchUiSettings, type UiSettings } from "./api";

/** Fetched once and kept. It only changes when the server restarts, so
 *  re-asking is exactly the kind of poll this module exists to remove. */
export function useUiSettings() {
  return useQuery({
    queryKey: ["ui-settings"],
    queryFn: fetchUiSettings,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

/**
 * An interval for `refetchInterval`, or `false` for "do not poll".
 *
 * `false` rather than a very large number, so React Query arms no timer at all
 * rather than a quiet one.
 */
export function useLive(which: keyof UiSettings): number | false {
  const { data } = useUiSettings();
  const setting = data?.[which];
  if (!setting?.enabled) return false;
  // A floor, because a setting of 0 or a negative would otherwise mean a tight
  // loop against the GPU box, and no typo should be able to do that.
  return Math.max(1, setting.seconds) * 1000;
}
